/**
 * auto-captions-auto-publish.js — "Publish" assistant for /auto-captions.
 *
 * Once captions exist, creators want one click that turns the transcript into
 * the metadata the host platform actually accepts: a title, a description,
 * chapter markers, and a hashtag string. Submagic charges for this; we do it
 * client-side from the transcript we already have in memory.
 *
 * No ML download. No compromise.cool fetch. The whole feature is a few
 * regexes + a multilingual stopwords list + a TF-IDF pass. Quality is
 * "Submagic Description Generator" parity — short, dense, host-platform
 * friendly — not "GPT-4 ghostwriter prose". That's a feature: it stays
 * deterministic and works offline.
 *
 * Public API (window.__acAutoPublish):
 *   generateTitle(words)                    → string  (≤70 chars, title-case)
 *   generateDescription(words)              → string  (~150-250 chars)
 *   generateChapters(words, vadSegments)    → Array<{ t: seconds, label: string }>
 *   generateHashtags(words[, n=8])          → Array<string>  ("#tag" prefixed)
 *   buildPublishPanel()                     → HTMLElement (full DOM tree)
 *
 * Lifecycle:
 *   - Loaded with `defer` AFTER pipeline.js so window.__acGetWords (if the
 *     pipeline ever exposes it) is reachable. We don't depend on it though —
 *     we read state via a small adapter.
 *   - Mounts into the #ac-card-publish container that auto-captions.blade
 *     ships. The container is `hidden` until the `ac-captions-ready`
 *     CustomEvent fires; we listen for the same event the multi-format module
 *     does and re-render so the panel reflects the latest transcript.
 *
 * Tested under tests/js/auto-captions-auto-publish.test.mjs.
 */
(function () {
    'use strict';

    /* ═══════════════════════════════════════════════════════════════
       MULTILINGUAL STOPWORDS
       ═══════════════════════════════════════════════════════════════
       Hand-curated short list per locale — covers ~95% of removable
       grammatical noise (articles, conjunctions, pronouns, very common
       verbs). Bigger isn't better here: we want a hashtag list that
       still surfaces topical nouns like "ableton" or "framework", and
       an aggressive stopwords list would knock those out as accidental
       English noise. */

    var STOPWORDS = new Set([
        // English
        'a','an','the','and','or','but','if','then','else','so','for','of','to','in','on','at','by','with','from','as','is','are','was','were','be','been','being','am','do','does','did','have','has','had','having','i','you','he','she','it','we','they','me','him','her','us','them','my','your','his','its','our','their','this','that','these','those','what','which','who','whom','whose','when','where','why','how','not','no','yes','yeah','ok','okay','um','uh','like','just','really','very','also','too','only','can','could','would','should','will','shall','may','might','must','one','two','three','any','some','all','more','most','here','there','now','still','again','about','into','out','up','down','off','over','under','than','then','because','while','well','get','got','make','made','go','going','went','say','said','says','know','knew','think','thought','want','wanted','need','needed','use','used','look','looks','take','took','give','gave','come','came','let','put','see','saw','seen','my','our','their',
        // Russian
        'и','в','во','не','что','он','на','я','с','со','как','а','то','все','она','так','его','но','да','ты','к','у','же','вы','за','бы','по','только','ее','мне','было','вот','от','меня','еще','нет','о','из','ему','теперь','когда','даже','ну','вдруг','ли','если','уже','или','ни','быть','был','него','до','вас','нибудь','опять','уж','вам','ведь','там','потом','себя','ничего','ей','может','они','тут','где','есть','надо','ней','для','мы','тебя','их','чем','была','сам','чтоб','без','будто','чего','раз','тоже','себе','под','будет','ж','тогда','кто','этот','говорил','того','потому','этого','какой','совсем','ним','здесь','эту','один','почти','мой','тем','чтобы','нее','сейчас','были','куда','зачем','всех','никогда','можно','при','наконец','два','об','другой','хоть','после','над','больше','тот','через','эти','нас','про','всего','них','какая','много','разве','эта','я','этом','мою','свою','свою','своя','свой','моя','моя','моё','наше','наша','наш','ваш','ваша','ваше','твой','твоя','твоё','очень','тоже',
        // French
        'le','la','les','un','une','des','du','de','et','ou','mais','donc','car','ni','que','qui','quoi','dont','où','je','tu','il','elle','on','nous','vous','ils','elles','me','te','se','lui','leur','mon','ma','mes','ton','ta','tes','son','sa','ses','notre','votre','leur','ce','cet','cette','ces','est','sont','suis','es','sommes','êtes','être','étais','était','étions','étaient','ai','as','a','avons','avez','ont','avoir','en','pour','par','avec','sans','sur','sous','dans','vers','comme','pas','plus','très','si','aussi','encore','déjà','toujours','jamais','bien','là','ici','où','quand','comment','pourquoi','oui','non','peut','peux','peuvent',
        // German
        'der','die','das','den','dem','des','ein','eine','einen','einem','einer','eines','und','oder','aber','denn','sondern','dass','weil','wenn','als','wie','was','wer','wem','wen','wo','wann','warum','wieso','ich','du','er','sie','es','wir','ihr','mein','dein','sein','ihr','unser','euer','dieser','diese','dieses','jener','jene','jenes','ist','sind','bin','bist','seid','war','waren','sein','habe','hast','hat','haben','hatten','werden','wird','wurde','wurden','nicht','kein','keine','keinen','nur','auch','noch','schon','sehr','mehr','sehr','weniger','weniger','immer','nie','dort','hier','jetzt','dann','also','so','ja','nein','mit','ohne','für','gegen','um','vor','nach','bei','aus','von','zu','zur','zum','in','an','auf','über','unter','durch','gegen',
        // Spanish
        'el','la','los','las','un','una','unos','unas','y','o','pero','si','no','sí','que','quien','quienes','cuyo','cuya','cuyos','cuyas','yo','tú','él','ella','nosotros','vosotros','ellos','ellas','me','te','se','lo','le','la','nos','os','les','mi','tu','su','nuestro','vuestro','mis','tus','sus','este','esta','estos','estas','ese','esa','esos','esas','aquel','aquella','aquellos','aquellas','es','son','soy','eres','somos','sois','era','eran','ser','estar','estoy','estás','está','estamos','estáis','están','he','has','ha','hemos','habéis','han','haber','de','en','con','por','para','sin','sobre','bajo','desde','hasta','contra','muy','más','menos','también','tan','así','ya','aún','aún','aún','solo','sólo','todavía','siempre','nunca','aquí','ahí','allí','ahora','luego','después','antes','cómo','cuándo','dónde','por qué','qué','cuál','cuáles','cuántos','cuántas',
        // Portuguese
        'o','a','os','as','um','uma','uns','umas','e','ou','mas','se','sim','não','que','quem','cujo','cuja','cujos','cujas','eu','tu','ele','ela','nós','vós','vocês','eles','elas','me','te','se','o','a','lhe','nos','vos','lhes','meu','minha','teu','tua','seu','sua','nosso','nossa','vosso','vossa','este','esta','estes','estas','esse','essa','esses','essas','aquele','aquela','aqueles','aquelas','é','são','sou','és','somos','sois','era','eram','ser','estar','estou','estás','está','estamos','estais','estão','tenho','tens','tem','temos','tendes','têm','ter','de','em','com','por','para','sem','sobre','sob','desde','até','contra','muito','mais','menos','também','tão','assim','já','ainda','só','apenas','sempre','nunca','aqui','aí','ali','agora','depois','antes','como','quando','onde','por que','que','qual','quais','quantos','quantas',
        // Indonesian
        'yang','di','ke','dari','dan','atau','tetapi','tapi','jika','kalau','karena','sebab','agar','supaya','meskipun','walaupun','saya','aku','kamu','anda','dia','beliau','kami','kita','mereka','ini','itu','sini','situ','sana','adalah','ialah','ada','tidak','tak','bukan','belum','sudah','akan','sedang','telah','pernah','sangat','sekali','sekali','sangat','agak','cukup','hanya','saja','juga','pun','memang','mau','ingin','bisa','dapat','harus','perlu','boleh','apa','siapa','mana','bagaimana','mengapa','kapan','berapa','dengan','untuk','tentang','seperti','seperti','seperti','oleh','pada','dalam','luar','atas','bawah','depan','belakang','samping','antara','antara','sambil','sambil',
        // Korean (particle-like fillers — not strictly stopwords but
        // common single-syllable tokens that shouldn't drive hashtags)
        '그리고','그러나','하지만','그래서','그런','이런','저런','이것','저것','그것','이','그','저','은','는','이','가','을','를','에','에서','으로','로','와','과','도','만','요','죠','네','입니다','이다','있다','없다','한다','된다','같다','수','것','때','때문','매우','정말','진짜','조금','약간','매우','너무','잘','못','안','아니','네','예','음',
        // Arabic
        'ال','و','أو','لكن','إذا','لو','حتى','كذلك','أيضا','لأن','بينما','رغم','مع','بدون','عن','على','إلى','من','في','هو','هي','هما','هم','هن','أنا','أنت','أنتم','نحن','هذا','هذه','ذلك','تلك','هؤلاء','أولئك','الذي','التي','اللذان','اللذين','اللتان','اللتين','الذين','اللاتي','ما','ماذا','من','أين','متى','كيف','لماذا','كم','نعم','لا','ليس','ليست','ليسوا','كان','كانت','يكون','تكون','يصبح','تصبح','قد','قال','قالت','يقول','تقول','أكثر','أقل','جدا','كثيرا','قليلا','الآن','هنا','هناك','بعد','قبل','عند','بعض','كل','إن','أن','إنما','لكي'
    ]);

    /* ═══════════════════════════════════════════════════════════════
       TOKENISATION
       ═══════════════════════════════════════════════════════════════ */

    function wordsToTokens(words) {
        if (!Array.isArray(words) || !words.length) return [];
        var out = [];
        for (var i = 0; i < words.length; i++) {
            var t = (words[i] && words[i].text) || '';
            t = String(t).toLowerCase();
            // Strip basic punctuation everywhere except CJK script.
            // We KEEP intra-word marks (apostrophes/hyphens) so "don't"
            // stays one token and "machine-learning" stays one token.
            t = t.replace(/[\.,;:!\?\(\)\[\]\{\}"«»„“”‟‘’`]/g, '');
            t = t.replace(/^[\-_'`]+|[\-_'`]+$/g, '');
            if (!t) continue;
            out.push(t);
        }
        return out;
    }

    function isContentToken(tok) {
        if (!tok) return false;
        if (STOPWORDS.has(tok)) return false;
        // Filler interjections + numeric-only.
        if (/^[0-9]+$/.test(tok)) return false;
        if (tok.length < 2) return false;
        return true;
    }

    /* ═══════════════════════════════════════════════════════════════
       SENTENCE SEGMENTATION
       ═══════════════════════════════════════════════════════════════
       Group words into sentences by terminal punctuation (. ! ? …) OR
       by a long inter-word gap (>0.9s) — Whisper drops the period more
       often than we'd like, so a gap-based fallback catches "ums" and
       held pauses without forcing the user to fix punctuation first.
       Returns an Array<{ text, start, end, words }>. */

    var SENT_END = /[\.\?\!…。！？؟]$/;

    function segmentSentences(words) {
        if (!Array.isArray(words) || !words.length) return [];
        var sents = [];
        var bucket = [];
        var lastEnd = 0;

        function flushBucket() {
            if (!bucket.length) return;
            var first = bucket[0];
            var last = bucket[bucket.length - 1];
            var text = bucket.map(function(w){ return (w && w.text) || ''; }).join(' ').replace(/\s+/g, ' ').trim();
            if (text) {
                sents.push({
                    text: text,
                    start: typeof first.start === 'number' ? first.start : 0,
                    end:   typeof last.end === 'number' ? last.end : 0,
                    words: bucket.slice()
                });
            }
            bucket = [];
        }

        for (var i = 0; i < words.length; i++) {
            var w = words[i] || {};
            var gap = (typeof w.start === 'number') ? (w.start - lastEnd) : 0;
            if (bucket.length && gap > 0.9) {
                flushBucket();
            }
            bucket.push(w);
            if (SENT_END.test(String(w.text || '').trim())) {
                flushBucket();
            }
            if (typeof w.end === 'number') lastEnd = w.end;
        }
        flushBucket();
        return sents;
    }

    /* ═══════════════════════════════════════════════════════════════
       TITLE-CASE
       ═══════════════════════════════════════════════════════════════ */

    var TITLE_CASE_SKIP = new Set([
        'a','an','the','and','or','but','of','to','in','on','at','by','for','with','from','as','is','it'
    ]);

    function titleCase(s) {
        if (!s) return '';
        var parts = String(s).split(/(\s+)/); // keep separators
        var first = true;
        for (var i = 0; i < parts.length; i++) {
            if (/^\s+$/.test(parts[i])) continue;
            var token = parts[i];
            // Don't lowercase ALL-CAPS acronyms (>=2 chars all caps).
            if (/^[A-ZА-Я]{2,}$/.test(token)) { first = false; continue; }
            var lower = token.toLowerCase();
            if (!first && TITLE_CASE_SKIP.has(lower)) {
                parts[i] = lower;
            } else {
                // Capitalise first alphanumeric character.
                parts[i] = lower.replace(/^([^a-zа-яё0-9]*)([a-zа-яё0-9])/i, function(_, pre, ch){
                    return pre + ch.toUpperCase();
                });
            }
            first = false;
        }
        return parts.join('');
    }

    /* ═══════════════════════════════════════════════════════════════
       TITLE
       ═══════════════════════════════════════════════════════════════
       Strategy: first complete sentence ≤70 chars, title-cased. If the
       first sentence is too long, take its leading clause (before the
       first comma) and trim to a word boundary ≤70 chars. */

    var TITLE_HARD_CAP = 70;

    function generateTitle(words) {
        var sents = segmentSentences(words);
        if (!sents.length) return '';
        var pick = '';
        for (var i = 0; i < sents.length && i < 4; i++) {
            var s = sents[i].text.replace(/[\.\!\?…]+$/g, '').trim();
            if (!s) continue;
            if (s.length <= TITLE_HARD_CAP) { pick = s; break; }
            // Otherwise try the leading clause.
            var clause = s.split(/[,;:]/)[0].trim();
            if (clause && clause.length <= TITLE_HARD_CAP) { pick = clause; break; }
            // Last resort: truncate to word boundary.
            var truncated = s.slice(0, TITLE_HARD_CAP);
            truncated = truncated.replace(/\s+\S*$/, '').trim();
            if (truncated.length >= 12) { pick = truncated; break; }
        }
        if (!pick) {
            // Fallback: stitch first few content words.
            var toks = wordsToTokens(words).filter(isContentToken).slice(0, 8);
            pick = toks.join(' ');
        }
        return titleCase(pick).slice(0, TITLE_HARD_CAP).trim();
    }

    /* ═══════════════════════════════════════════════════════════════
       DESCRIPTION
       ═══════════════════════════════════════════════════════════════
       2-sentence summary. Sentence 1 is the first non-trivial sentence
       (>=4 content tokens). Sentence 2 is the highest-TF-IDF sentence
       from the rest (so the description surfaces a topical follow-up,
       not just the next opener). Target 150-250 chars. */

    function tokenSet(text) {
        var set = new Set();
        var raw = String(text || '').toLowerCase().split(/[\s.,;:!\?…。！？؟()\[\]"«»„“”‟‘’`]+/);
        for (var i = 0; i < raw.length; i++) {
            var t = raw[i];
            if (isContentToken(t)) set.add(t);
        }
        return set;
    }

    function computeIdf(sentences) {
        var df = Object.create(null);
        for (var i = 0; i < sentences.length; i++) {
            var seen = tokenSet(sentences[i].text);
            seen.forEach(function(tok){ df[tok] = (df[tok] || 0) + 1; });
        }
        var n = sentences.length;
        var idf = Object.create(null);
        for (var k in df) {
            if (Object.prototype.hasOwnProperty.call(df, k)) {
                idf[k] = Math.log(1 + n / df[k]);
            }
        }
        return idf;
    }

    function sentenceScore(sent, idf) {
        var seen = tokenSet(sent.text);
        var score = 0;
        seen.forEach(function(tok){ score += idf[tok] || 0; });
        // Penalise very short sentences (low information density).
        if (seen.size < 4) score *= 0.4;
        return score;
    }

    var DESC_MIN = 100;
    var DESC_MAX = 260;

    function generateDescription(words) {
        var sents = segmentSentences(words);
        if (!sents.length) return '';

        var idf = computeIdf(sents);

        // 1) Lead: first sentence with >=4 content tokens.
        var lead = null;
        for (var i = 0; i < sents.length; i++) {
            if (tokenSet(sents[i].text).size >= 4) { lead = sents[i]; break; }
        }
        if (!lead) lead = sents[0];

        // 2) Pick highest-TF-IDF sentence from the rest, preferring
        //    something later in the transcript so the description
        //    doesn't repeat the lead.
        var best = null;
        var bestScore = -1;
        for (var j = 0; j < sents.length; j++) {
            if (sents[j] === lead) continue;
            var sc = sentenceScore(sents[j], idf);
            if (sc > bestScore) { bestScore = sc; best = sents[j]; }
        }

        function tidy(s) {
            return String(s || '').replace(/\s+/g, ' ').trim();
        }
        function ensureTerminal(s) {
            if (!s) return '';
            return SENT_END.test(s) ? s : (s + '.');
        }

        var leadText = ensureTerminal(tidy(lead.text));
        var bestText = best ? ensureTerminal(tidy(best.text)) : '';

        var combined = bestText ? (leadText + ' ' + bestText) : leadText;

        // Trim within range. We never break mid-word; if too long, drop
        // sentence 2 and truncate sentence 1.
        if (combined.length > DESC_MAX) {
            if (leadText.length <= DESC_MAX) {
                combined = leadText;
            } else {
                var t = leadText.slice(0, DESC_MAX);
                t = t.replace(/\s+\S*$/, '').trim();
                combined = ensureTerminal(t);
            }
        }
        // If we are well under DESC_MIN and there's room for a third
        // high-scoring sentence, append it. Cheap quality bump.
        if (combined.length < DESC_MIN) {
            var ranked = sents.slice().filter(function(s){ return s !== lead && s !== best; });
            ranked.sort(function(a, b){ return sentenceScore(b, idf) - sentenceScore(a, idf); });
            for (var k = 0; k < ranked.length; k++) {
                var add = ensureTerminal(tidy(ranked[k].text));
                if (combined.length + 1 + add.length <= DESC_MAX) {
                    combined = combined + ' ' + add;
                    if (combined.length >= DESC_MIN) break;
                }
            }
        }
        return combined;
    }

    /* ═══════════════════════════════════════════════════════════════
       CHAPTERS
       ═══════════════════════════════════════════════════════════════
       Boundary candidates:
         - VAD pause >= 2.0s
         - Inter-word gap >= 1.5s if VAD missing
       Each chapter must be ≥20s long (YouTube's minimum is 10s, we leave
       headroom). First chapter starts at 0:00 (YouTube requires this).
       Label = title-case of first 6 content tokens of the chapter. */

    var CHAPTER_MIN_SEC = 10;        // YouTube hard floor; we ride it for short clips
    var CHAPTER_TARGET_SEC = 20;      // preferred minimum on longer videos
    var CHAPTER_VAD_GAP = 2.0;
    var CHAPTER_WORD_GAP = 1.5;

    function chapterLabel(chapterWords) {
        var tokens = [];
        for (var i = 0; i < chapterWords.length && tokens.length < 6; i++) {
            var raw = (chapterWords[i] && chapterWords[i].text) || '';
            var clean = String(raw).replace(/[\.,;:!\?…]+/g, '').trim();
            if (!clean) continue;
            tokens.push(clean);
        }
        if (!tokens.length) return 'Intro';
        return titleCase(tokens.join(' '));
    }

    function generateChapters(words, vadSegments) {
        if (!Array.isArray(words) || !words.length) return [];

        // 1) Find boundary candidates as word INDICES where a new
        //    chapter starts. We always seed the chapter at index 0.
        var boundaries = [0];

        // Prefer VAD gaps if provided. VAD segments come as
        //   [{ start, end }, ...] in seconds, sorted ascending. A "gap"
        //   between two consecutive segments where (next.start -
        //   prev.end) >= CHAPTER_VAD_GAP becomes a candidate. We translate
        //   the gap end-time back into a word index (first word whose
        //   .start >= gapEnd).
        var usedVad = false;
        if (Array.isArray(vadSegments) && vadSegments.length >= 2) {
            usedVad = true;
            for (var v = 1; v < vadSegments.length; v++) {
                var prev = vadSegments[v - 1];
                var next = vadSegments[v];
                if (!prev || !next) continue;
                var pE = +prev.end || 0;
                var nS = +next.start || 0;
                if (nS - pE >= CHAPTER_VAD_GAP) {
                    var idx = -1;
                    for (var w = 0; w < words.length; w++) {
                        if ((+words[w].start || 0) >= nS) { idx = w; break; }
                    }
                    if (idx > 0) boundaries.push(idx);
                }
            }
        }

        // 2) Fallback: inter-word gaps if VAD wasn't useful.
        if (!usedVad || boundaries.length < 2) {
            for (var i = 1; i < words.length; i++) {
                var gap = (+words[i].start || 0) - (+words[i - 1].end || 0);
                if (gap >= CHAPTER_WORD_GAP) {
                    if (boundaries.indexOf(i) === -1) boundaries.push(i);
                }
            }
        }

        boundaries.sort(function(a, b){ return a - b; });

        // 3) Enforce minimum chapter length. We prefer CHAPTER_TARGET_SEC,
        //    but for short clips (where the whole transcript wouldn't even
        //    yield 2 chapters at 20s spacing) we relax to CHAPTER_MIN_SEC
        //    so short tutorials still get chapter markers.
        var totalSec = (+words[words.length - 1].end || 0) - (+words[0].start || 0);
        var minGap = (totalSec < CHAPTER_TARGET_SEC * 2.5) ? CHAPTER_MIN_SEC : CHAPTER_TARGET_SEC;

        var chapters = [];
        var pendingStart = boundaries[0];
        for (var b = 1; b < boundaries.length; b++) {
            var bi = boundaries[b];
            var startSec = +words[pendingStart].start || 0;
            var thisSec = +words[bi].start || 0;
            if (thisSec - startSec >= minGap) {
                chapters.push({ startIdx: pendingStart, endIdx: bi - 1 });
                pendingStart = bi;
            }
        }
        chapters.push({ startIdx: pendingStart, endIdx: words.length - 1 });

        // 4) Materialise { t, label } per chapter.
        var out = [];
        for (var c = 0; c < chapters.length; c++) {
            var ch = chapters[c];
            var t = c === 0 ? 0 : Math.max(0, +words[ch.startIdx].start || 0);
            var chunk = words.slice(ch.startIdx, ch.endIdx + 1);
            out.push({ t: t, label: chapterLabel(chunk) });
        }
        // Always at least 1 chapter, starting at 0.
        if (!out.length) out.push({ t: 0, label: chapterLabel(words.slice(0, 8)) });
        // Force first chapter to t=0 (YouTube requirement).
        out[0].t = 0;
        return out;
    }

    /* ═══════════════════════════════════════════════════════════════
       HASHTAGS — TF-IDF on tokens, top-N nouns-ish
       ═══════════════════════════════════════════════════════════════
       We approximate "nouns" by stripping stopwords + numerics + very
       short tokens. We then rank by raw frequency × IDF-ish weight,
       where IDF comes from a tiny built-in "common English/RU/etc."
       prior so super-common but non-stop tokens (today, week, video)
       don't sit above topical ones (kubernetes, ableton). */

    var COMMON_PRIOR = new Set([
        // Generic time/quantity words
        'today','yesterday','tomorrow','week','weeks','month','months','year','years','minute','minutes','hour','hours','second','seconds','time','times','day','days','night','morning',
        'video','videos','channel','content','people','thing','things','stuff','example','examples','case','cases','way','ways','part','parts','idea','ideas','point','points','problem','problems','reason','reasons','word','words','question','questions',
        // RU equivalents
        'сегодня','вчера','завтра','неделя','месяц','год','час','минута','секунда','время','день','ночь','утро','видео','канал','пример','случай','часть','идея','вопрос','причина',
        // ES/PT/FR/DE/IT generics we don't want as hashtags
        'hoy','ayer','mañana','día','semana','mes','año','tiempo','video','ejemplo','vídeo','exemple','journée','semaine','mois','année','beispiel','heute','tag','woche','monat','jahr','zeit','beispiele',
        // ID
        'hari','minggu','bulan','tahun','waktu','contoh','soal',
    ]);

    function generateHashtags(words, n) {
        var top = (n | 0) > 0 ? (n | 0) : 8;
        var toks = wordsToTokens(words).filter(isContentToken);
        if (!toks.length) return [];

        // Frequency
        var freq = Object.create(null);
        for (var i = 0; i < toks.length; i++) {
            freq[toks[i]] = (freq[toks[i]] || 0) + 1;
        }

        // Score: freq * priorPenalty * lengthBonus
        var ranked = [];
        for (var k in freq) {
            if (!Object.prototype.hasOwnProperty.call(freq, k)) continue;
            if (freq[k] < 2) continue;                          // Drop one-off junk
            var penalty = COMMON_PRIOR.has(k) ? 0.35 : 1;
            var bonus = Math.min(1.3, 0.8 + k.length * 0.04);   // Slight bias toward longer terms
            ranked.push({ tok: k, score: freq[k] * penalty * bonus });
        }
        ranked.sort(function(a, b){ return b.score - a.score; });

        // Dedupe near-duplicates ("python" vs "pythons" — keep longer-freq winner)
        var seen = new Set();
        var out = [];
        for (var j = 0; j < ranked.length && out.length < top; j++) {
            var t = ranked[j].tok;
            // Strip intra-word punctuation for the hashtag form.
            var hashed = t.replace(/[\-_'’`]+/g, '');
            if (!hashed) continue;
            if (seen.has(hashed)) continue;
            seen.add(hashed);
            out.push('#' + hashed);
        }
        return out;
    }

    /* ═══════════════════════════════════════════════════════════════
       i18n bootstrap — read inline JSON the blade injects.
       Fallback gives the module sensible defaults if the blade hasn't
       wired in translations (e.g. the node-side test).
       ═══════════════════════════════════════════════════════════════ */

    function I() {
        var i = (typeof window !== 'undefined' && window.__acPublishI18N) || {};
        function pick(key, def) { return (i[key] != null && String(i[key]).length) ? i[key] : def; }
        return {
            title:            pick('publish_title',            'Publish'),
            subtitle:         pick('publish_subtitle',         'Ready-to-paste title, description, chapters and hashtags.'),
            section_title:    pick('publish_section_title',    'Title'),
            section_desc:     pick('publish_section_desc',     'Description'),
            section_chapters: pick('publish_section_chapters', 'Chapters'),
            section_hashtags: pick('publish_section_hashtags', 'Hashtags'),
            copy_btn:         pick('publish_copy_btn',         'Copy'),
            copy_all_btn:     pick('publish_copy_all_btn',     'Copy all'),
            copied_toast:     pick('publish_copied_toast',     'Copied to clipboard'),
        };
    }

    /* ═══════════════════════════════════════════════════════════════
       UI — buildPublishPanel() / mounting
       ═══════════════════════════════════════════════════════════════ */

    function fmtTime(s) {
        s = Math.max(0, Math.round(+s || 0));
        var m = Math.floor(s / 60), sec = s % 60;
        var h = Math.floor(m / 60); m = m % 60;
        function pad(x){ return x < 10 ? '0' + x : '' + x; }
        return h > 0 ? (h + ':' + pad(m) + ':' + pad(sec)) : (m + ':' + pad(sec));
    }

    function getWords() {
        // The blade exposes window.__acState (the mutable pipeline state)
        // and historically window.__acGetWords() helpers. Try both so we
        // survive refactors of either side.
        try {
            if (typeof window.__acGetWords === 'function') {
                var w1 = window.__acGetWords();
                if (Array.isArray(w1) && w1.length) return w1;
            }
        } catch(e){}
        try {
            if (window.__acState && Array.isArray(window.__acState.words) && window.__acState.words.length) {
                return window.__acState.words;
            }
        } catch(e){}
        try {
            if (window.__acDebug && window.__acDebug.state && Array.isArray(window.__acDebug.state.words)) {
                return window.__acDebug.state.words;
            }
        } catch(e){}
        return [];
    }
    function getVad() {
        try {
            if (window.__acState) {
                if (Array.isArray(window.__acState.vadSegments)) return window.__acState.vadSegments;
                if (window.__acState.vadInfo && Array.isArray(window.__acState.vadInfo.segments)) return window.__acState.vadInfo.segments;
            }
        } catch(e){}
        try {
            if (window.__acDebug && window.__acDebug.state && Array.isArray(window.__acDebug.state.vadSegments)) {
                return window.__acDebug.state.vadSegments;
            }
        } catch(e){}
        return null;
    }

    function copyText(text) {
        if (!text) return Promise.resolve(false);
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
            return navigator.clipboard.writeText(text).then(function(){ return true; }, function(){ return fallbackCopy(text); });
        }
        return Promise.resolve(fallbackCopy(text));
    }
    function fallbackCopy(text) {
        try {
            var ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'absolute';
            ta.style.left = '-9999px';
            document.body.appendChild(ta);
            ta.select();
            var ok = document.execCommand('copy');
            document.body.removeChild(ta);
            return ok;
        } catch (e) { return false; }
    }

    function toast(msg) {
        try {
            if (typeof window.__toast === 'function') { window.__toast(msg, 'success'); return; }
        } catch(e){}
    }

    function makeSection(labelText, bodyEl, onCopy) {
        var box = document.createElement('div');
        box.className = 'ac-pub-section';
        box.style.cssText = 'margin-top:12px;padding:10px 12px;border:1px solid var(--border, #2c3340);border-radius:10px;background:var(--glass, rgba(255,255,255,0.02))';

        var head = document.createElement('div');
        head.className = 'ac-pub-section-head';
        head.style.cssText = 'display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;font-size:12px;font-weight:600;color:var(--text-secondary, #b4bdc7);';

        var h = document.createElement('span');
        h.textContent = labelText;
        head.appendChild(h);

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'ac-btn secondary ac-pub-copy';
        btn.style.cssText = 'font-size:12px;padding:4px 10px;';
        btn.textContent = I().copy_btn;
        btn.addEventListener('click', function(){
            Promise.resolve(onCopy()).then(function(ok){
                if (ok) toast(I().copied_toast);
            });
        });
        head.appendChild(btn);

        box.appendChild(head);
        box.appendChild(bodyEl);
        return box;
    }

    function buildPublishPanel() {
        var words = getWords();
        var vad = getVad();
        var i = I();

        var root = document.createElement('div');
        root.className = 'ac-pub-root';
        root.dataset.acPub = '1';

        // Header
        var head = document.createElement('div');
        head.className = 'ac-pub-head';
        head.style.cssText = 'display:flex;align-items:flex-start;justify-content:space-between;gap:12px;';
        var headLeft = document.createElement('div');
        var sub = document.createElement('div');
        sub.style.cssText = 'font-size:12px;color:var(--text-muted, #8995a3);margin-top:2px;';
        sub.textContent = i.subtitle;
        headLeft.appendChild(sub);
        head.appendChild(headLeft);

        var copyAllBtn = document.createElement('button');
        copyAllBtn.type = 'button';
        copyAllBtn.className = 'ac-btn ac-pub-copy-all';
        copyAllBtn.style.cssText = 'font-size:12px;padding:6px 12px;flex-shrink:0;';
        copyAllBtn.textContent = i.copy_all_btn;
        head.appendChild(copyAllBtn);
        root.appendChild(head);

        // Generate values
        var title = generateTitle(words);
        var desc  = generateDescription(words);
        var chs   = generateChapters(words, vad);
        var tags  = generateHashtags(words, 8);

        // Title (editable textarea, single-line)
        var titleInput = document.createElement('input');
        titleInput.type = 'text';
        titleInput.className = 'ac-pub-title-input';
        titleInput.value = title;
        titleInput.maxLength = 200;
        titleInput.style.cssText = 'width:100%;padding:8px 10px;border:1px solid var(--border, #2c3340);border-radius:8px;background:var(--bg-tertiary, #1a212c);color:var(--text-primary, #e6ecf3);font-size:13px;';
        root.appendChild(makeSection(i.section_title, titleInput, function(){ return copyText(titleInput.value || ''); }));

        // Description (editable textarea)
        var descArea = document.createElement('textarea');
        descArea.className = 'ac-pub-desc-area';
        descArea.value = desc;
        descArea.rows = 4;
        descArea.style.cssText = 'width:100%;padding:8px 10px;border:1px solid var(--border, #2c3340);border-radius:8px;background:var(--bg-tertiary, #1a212c);color:var(--text-primary, #e6ecf3);font-size:13px;font-family:inherit;resize:vertical;min-height:72px;';
        root.appendChild(makeSection(i.section_desc, descArea, function(){ return copyText(descArea.value || ''); }));

        // Chapters (read-only list of t — label)
        var chList = document.createElement('ol');
        chList.className = 'ac-pub-chapters';
        chList.style.cssText = 'list-style:none;padding:0;margin:0;display:flex;flex-direction:column;gap:4px;font-size:13px;color:var(--text-primary, #e6ecf3);';
        var chPlain = chs.map(function(c){ return fmtTime(c.t) + ' ' + c.label; }).join('\n');
        for (var ci = 0; ci < chs.length; ci++) {
            var li = document.createElement('li');
            li.style.cssText = 'display:flex;gap:8px;';
            var tEl = document.createElement('span');
            tEl.textContent = fmtTime(chs[ci].t);
            tEl.style.cssText = 'min-width:60px;color:var(--accent, #5eb1ff);font-variant-numeric:tabular-nums;';
            var lEl = document.createElement('span');
            lEl.textContent = chs[ci].label;
            li.appendChild(tEl);
            li.appendChild(lEl);
            chList.appendChild(li);
        }
        root.appendChild(makeSection(i.section_chapters, chList, function(){ return copyText(chPlain); }));

        // Hashtags
        var tagRow = document.createElement('div');
        tagRow.className = 'ac-pub-hashtags';
        tagRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;';
        for (var ti = 0; ti < tags.length; ti++) {
            var chip = document.createElement('span');
            chip.className = 'ac-pub-tag';
            chip.textContent = tags[ti];
            chip.style.cssText = 'display:inline-block;padding:3px 10px;border-radius:999px;background:var(--glass-hover, rgba(94,177,255,0.12));color:var(--accent, #5eb1ff);font-size:12px;border:1px solid var(--border, #2c3340);';
            tagRow.appendChild(chip);
        }
        var tagPlain = tags.join(' ');
        root.appendChild(makeSection(i.section_hashtags, tagRow, function(){ return copyText(tagPlain); }));

        // Wire Copy-all
        copyAllBtn.addEventListener('click', function(){
            var bundle = [
                (titleInput.value || '').trim(),
                '',
                (descArea.value || '').trim(),
                '',
                chPlain,
                '',
                tagPlain
            ].join('\n');
            Promise.resolve(copyText(bundle)).then(function(ok){ if (ok) toast(I().copied_toast); });
        });

        return root;
    }

    /* ═══════════════════════════════════════════════════════════════
       Mount lifecycle
       ═══════════════════════════════════════════════════════════════ */

    function mountInto(container) {
        if (!container) return;
        // Clean prior render (allows refresh after re-transcribe).
        var prior = container.querySelector('[data-ac-pub="1"]');
        if (prior) prior.parentNode.removeChild(prior);
        container.appendChild(buildPublishPanel());
        container.hidden = false;
    }

    function tryMount() {
        if (typeof document === 'undefined') return;
        var body = document.getElementById('ac-card-publish-body');
        var card = document.getElementById('ac-card-publish');
        if (!body || !card) return;
        var words = getWords();
        if (!words || !words.length) return;
        card.hidden = false;
        mountInto(body);
    }

    if (typeof window !== 'undefined') {
        window.__acAutoPublish = {
            generateTitle: generateTitle,
            generateDescription: generateDescription,
            generateChapters: generateChapters,
            generateHashtags: generateHashtags,
            buildPublishPanel: buildPublishPanel,
        };

        if (typeof document !== 'undefined') {
            // Pipeline raises ac-captions-ready when state.words is populated.
            window.addEventListener('ac-captions-ready', tryMount);
            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', tryMount);
            } else {
                tryMount();
            }
        }
    }

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = {
            generateTitle: generateTitle,
            generateDescription: generateDescription,
            generateChapters: generateChapters,
            generateHashtags: generateHashtags,
            buildPublishPanel: buildPublishPanel,
        };
    }
})();
